import { createSlice } from '@reduxjs/toolkit';
import { authApi } from './authApi';
import type { CurrentUser } from '@mmef/shared/api';

/**
 * Session state, derived from the API rather than owned separately.
 *
 * The slice never fetches anything itself — it listens to the auth endpoints' lifecycle matchers.
 * That keeps one source of truth (the server) while giving the router a synchronous answer to
 * "is this person signed in?", which a `useQuery` hook alone can't provide during the first paint.
 *
 * `status` distinguishes "we haven't asked yet" from "we asked and they're anonymous". Without
 * that, guarded routes bounce a signed-in user to the sign-in page on every hard refresh.
 */
export type AuthStatus = 'unknown' | 'authenticated' | 'anonymous';

interface AuthState {
  user: CurrentUser | null;
  status: AuthStatus;
}

const initialState: AuthState = { user: null, status: 'unknown' };

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /**
     * Explicitly forget the session.
     *
     * <p>Exists because this slice used to import the public app's account API to notice a
     * deletion — shared code reaching into one particular app, which broke the moment a second
     * app needed it. Now anything that ends a session says so directly.
     */
    sessionCleared(state) {
      state.user = null;
      state.status = 'anonymous';
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(authApi.endpoints.getMe.matchFulfilled, (state, { payload }) => {
        state.user = payload;
        state.status = 'authenticated';
      })
      .addMatcher(authApi.endpoints.getMe.matchRejected, (state) => {
        // A 401 here is the normal answer for a visitor, not an error worth surfacing.
        state.user = null;
        state.status = 'anonymous';
      })
      .addMatcher(authApi.endpoints.verifyMagicLink.matchFulfilled, (state, { payload }) => {
        state.user = payload;
        state.status = 'authenticated';
      })
      .addMatcher(authApi.endpoints.signOut.matchFulfilled, (state) => {
        state.user = null;
        state.status = 'anonymous';
      });
  },
});

export const { sessionCleared } = authSlice.actions;

export default authSlice.reducer;

export const selectCurrentUser = (state: { auth: AuthState }) => state.auth.user;
export const selectAuthStatus = (state: { auth: AuthState }) => state.auth.status;
export const selectIsAdmin = (state: { auth: AuthState }) =>
  state.auth.user?.roles.includes('ROLE_ADMIN') ?? false;
